import { SlidersHorizontal, X, CaretDown, Tag, Medal, SealPercent } from '@phosphor-icons/react';
import { ImagenRecortada, buildImageUrl } from './ProductoMeta';

// Single-restaurant: filtros sobre los productos del menú.
const FILTER_KEYS = ['maxPrice', 'onlyBestseller', 'onlyDiscount'];

// Tipo, label e icono por defecto de cada pill. El icono por defecto va en un
// cuadrito con su tinta; si el admin sube imagen (Business.filterIcons), manda
// la imagen. Los range filters muestran flecha cuando no tienen valor, y el
// valor seleccionado + X cuando sí lo tienen.
const PILL_CONFIG = {
  maxPrice:     { type: 'range',   label: 'Por menos de',  Icono: Tag,         tinta: 'bg-amber-50 text-amber-600' },
  onlyBestseller: { type: 'boolean', label: 'Más vendidos', Icono: Medal,     tinta: 'bg-orange-50 text-orange-500' },
  onlyDiscount: { type: 'boolean', label: 'Con descuento', Icono: SealPercent, tinta: 'bg-rose-50 text-rose-500' },
};

const isFilterActive = (key, value) => {
  if (PILL_CONFIG[key]?.type === 'boolean') return !!value;
  return value != null;
};

// Lado del cuadrito del icono, en px. Con 4px de aire arriba y abajo da un
// pill de 36px. El dibujo va adentro, a lo mucho DIBUJO_ICONO por lado.
const LADO_ICONO = 28;
const DIBUJO_ICONO = 16;

// Caja común de todos los pills: blanca, borde fino y una sombra apenas
// perceptible para despegarla del fondo. El texto es el de siempre (13px,
// seminegrita); el icono va pegado a la orilla izquierda.
const pillClase = (activo) =>
  `flex items-center gap-1.5 h-9 pl-1 pr-3 rounded-xl border flex-shrink-0 text-[13px] font-semibold whitespace-nowrap shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:scale-95 transition-all ${
    activo ? 'border-primary-600 bg-primary-100 text-primary-900' : 'border-gray-200 bg-white text-gray-900'
  }`;

/**
 * Icono del pill, siempre en su cuadrito de color (como en la referencia).
 * Adentro, la imagen del dashboard —recortada a su dibujo y a lo mucho
 * DIBUJO_ICONO por lado, para que un PNG con aire o muy ancho no se desborde—
 * o, sin ella, el icono por defecto.
 */
function IconoFiltro({ imagen, Icono, tinta }) {
  return (
    <span
      className={`rounded-lg flex items-center justify-center flex-shrink-0 ${tinta}`}
      style={{ width: LADO_ICONO, height: LADO_ICONO }}
    >
      {imagen ? (
        <ImagenRecortada url={buildImageUrl(imagen)} alt="" alto={DIBUJO_ICONO} maxAncho={DIBUJO_ICONO} />
      ) : (
        <Icono size={DIBUJO_ICONO} weight="fill" />
      )}
    </span>
  );
}

/**
 * Fila horizontal de pills de filtro del menú: cada pill es una tarjetita
 * blanca con su icono a la izquierda y el nombre del filtro.
 *   - "Filtros": abre FiltersModal (badge con contador si hay activos).
 *   - Boolean pills (onlyBestseller, onlyDiscount): toggle directo. Activo muestra X.
 *   - Range pill (maxPrice): muestra flecha cuando vacío; tap abre
 *     RangeFilterSheet. Con valor se ve "Por menos de $100" + X (tap clear).
 *
 * Props:
 *   filters: { maxPrice, onlyBestseller, onlyDiscount, sortBy }
 *   iconos: Business.filterIcons — { filtros, maxPrice, onlyBestseller, onlyDiscount } (URLs)
 *   onSetValue: (key, value) => void  // boolean toggle o number/null para range
 *   onOpenRangeSheet: (key) => void   // abre sheet para elegir rango
 *   onOpenModal: () => void           // abre FiltersModal completo
 */
export default function FilterPills({ filters, iconos = {}, onSetValue, onOpenRangeSheet, onOpenModal }) {
  // Activos primero, inactivos después.
  const sortedKeys = [...FILTER_KEYS].sort((a, b) => {
    const aActive = isFilterActive(a, filters[a]) ? 0 : 1;
    const bActive = isFilterActive(b, filters[b]) ? 0 : 1;
    return aActive - bActive;
  });

  // El orden cuenta como filtro activo para el badge, pero no tiene pill propio.
  const activeCount =
    FILTER_KEYS.filter((k) => isFilterActive(k, filters[k])).length +
    (filters.sortBy && filters.sortBy !== 'relevance' ? 1 : 0);

  const handlePillTap = (key) => {
    const config = PILL_CONFIG[key];
    if (config.type === 'boolean') {
      onSetValue(key, !filters[key]);
      return;
    }
    // Range filter: si tiene valor, tap = clear. Si no, tap = abrir sheet.
    if (filters[key] != null) {
      onSetValue(key, null);
    } else {
      onOpenRangeSheet(key);
    }
  };

  return (
    <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide px-3 pt-3 pb-2.5">
      <button type="button" onClick={onOpenModal} className={pillClase(false)} aria-label="Abrir filtros">
        <IconoFiltro imagen={iconos?.filtros} Icono={SlidersHorizontal} tinta="bg-gray-100 text-gray-700" />
        <span>Filtros</span>
        {activeCount > 0 && (
          <span className="w-4 h-4 bg-primary-800 text-white rounded-full text-[10px] font-bold flex items-center justify-center">
            {activeCount}
          </span>
        )}
      </button>

      {sortedKeys.map((key) => {
        const config = PILL_CONFIG[key];
        const value = filters[key];
        const active = isFilterActive(key, value);
        // Range activo muestra el valor: "Por menos de $100".
        const label = config.type === 'range' && active ? `${config.label} $${value}` : config.label;
        return (
          <button key={key} type="button" onClick={() => handlePillTap(key)} className={pillClase(active)}>
            <IconoFiltro imagen={iconos?.[key]} Icono={config.Icono} tinta={config.tinta} />
            <span>{label}</span>
            {active && <X size={12} weight="bold" />}
            {config.type === 'range' && !active && <CaretDown size={12} weight="bold" className="text-gray-500" />}
          </button>
        );
      })}
    </div>
  );
}
